/**
 * i18n configuration for the ProperHumanDiet frontend.
 *
 * Mirrors the shared i18n config at @shared/i18n but lives in the frontend
 * tree so TypeScript module resolution works without cross-project hacks.
 * Keep in sync with api/apps/_shared_frontend/i18n/config.ts.
 */
import i18n from "i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

/** Supported languages with metadata. */
export const languages = {
  ar: { name: "\u0627\u0644\u0639\u0631\u0628\u064A\u0629", dir: "rtl" as const },
  en: { name: "English", dir: "ltr" as const },
  fr: { name: "Fran\u00E7ais", dir: "ltr" as const },
  de: { name: "Deutsch", dir: "ltr" as const },
  es: { name: "Espa\u00F1ol", dir: "ltr" as const },
} as const;

export const supportedLangs = Object.keys(languages) as Array<keyof typeof languages>;
export type SupportedLanguage = keyof typeof languages;

const fallbackLng = "en";
const runtimeAssetBase = import.meta.env.BASE_URL;
// Locale files ship unhashed, so the build stamp is what makes a copy edit
// reach a browser that already cached the old bundle.
const localeCacheBust =
  typeof __I18N_BUILD__ === "undefined" ? "dev" : __I18N_BUILD__;
export const frontendNamespaces = [
  "analytics",
  "auth",
  "byok",
  "common",
  "community",
  "dashboard",
  "dataExport",
  "diet",
  "downloads",
  "errors",
  "experiments",
  "files",
  "health-assistant",
  "knowledge",
  "landing",
  "legal",
  "lifestyle",
  "medical",
  "mobile",
  "notifications",
  "profile",
  "pwa",
  "reminders",
  "seo",
  "store",
  "supplements",
  "symptoms",
  "sync",
  "treatments",
  "users",
  "waitlist",
] as const;
const globalI18nState = globalThis as typeof globalThis & {
  __PHD_I18N_INIT_PROMISE__?: Promise<unknown>;
  __PHD_I18N_LANGUAGE_SYNC_BOUND__?: boolean;
};

export function normalizeLanguage(lng?: string | null): SupportedLanguage {
  const lang = lng?.split("-")[0] as SupportedLanguage | undefined;
  return lang && supportedLangs.includes(lang) ? lang : "en";
}

function storeLanguage(lng: string): void {
  if (typeof window === "undefined") return;
  const lang = normalizeLanguage(lng);
  localStorage.setItem("i18nextLng", lang);
  const dir = languages[lang].dir;
  document.documentElement.dir = dir;
  document.documentElement.lang = lang;
  document.documentElement.style.direction = dir;
}

function getPathLanguage(): SupportedLanguage | null {
  if (typeof window === "undefined") return null;
  const firstSegment = window.location.pathname.split("/").filter(Boolean)[0];
  return supportedLangs.includes(firstSegment as SupportedLanguage)
    ? (firstSegment as SupportedLanguage)
    : null;
}

if (!i18n.isInitialized && !globalI18nState.__PHD_I18N_INIT_PROMISE__) {
  globalI18nState.__PHD_I18N_INIT_PROMISE__ = i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
    detection: {
      order: ["path", "localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupFromPathIndex: 0,
      lookupLocalStorage: "i18nextLng",
    },
    fallbackLng,
    load: "languageOnly",
    supportedLngs: supportedLangs,
    cleanCode: true,
    debug: import.meta.env.DEV,
    interpolation: { escapeValue: false },
    backend: {
      loadPath: `${runtimeAssetBase}locales/{{lng}}/{{ns}}.json?v=${localeCacheBust}`,
    },
    ns: frontendNamespaces,
    defaultNS: "common",
      partialBundledLanguages: true,
    })
    .catch((error: unknown) => {
      globalI18nState.__PHD_I18N_INIT_PROMISE__ = undefined;
      throw error;
    });
}

if (!globalI18nState.__PHD_I18N_LANGUAGE_SYNC_BOUND__) {
  i18n.on("languageChanged", (lng: string) => storeLanguage(lng));
  globalI18nState.__PHD_I18N_LANGUAGE_SYNC_BOUND__ = true;
}

// Set direction on first load.
if (typeof window !== "undefined") {
  const currentLang = normalizeLanguage(
    getPathLanguage() || localStorage.getItem("i18nextLng") || i18n.language || "en",
  );
  const dir = languages[currentLang].dir;
  document.documentElement.dir = dir;
  document.documentElement.lang = currentLang;
  document.documentElement.style.direction = dir;
}

export const changeLanguage = (lng: SupportedLanguage) => i18n.changeLanguage(lng);

export const getCurrentLanguage = (): SupportedLanguage =>
  normalizeLanguage(i18n.language);

export const isRTL = (lng?: string): boolean => {
  const lang = normalizeLanguage(lng || i18n.language);
  return languages[lang].dir === "rtl";
};

export const getLanguageInfo = (lng?: string) => {
  const lang = normalizeLanguage(lng || i18n.language);
  return languages[lang];
};

export default i18n;
